import React from 'react';
import { Box, Typography, Fade } from '@mui/material';
import { keyframes } from '@mui/system';

// Animated keyframes
const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.95); }
`;

const fadeInOut = keyframes`
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
`;

interface LoadingAnimationProps {
  message?: string;
  subtitle?: string;
}

export const LoadingAnimation: React.FC<LoadingAnimationProps> = ({
  message = 'Loading your dashboard...',
  subtitle = 'Preparing fresh data'
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '60vh',
        gap: 3,
      }}
    >
      {/* Animated BiteHub Logo SVG */}
      <Box
        sx={{
          position: 'relative',
          width: { xs: 200, sm: 250, md: 300 },
          height: { xs: 200, sm: 250, md: 300 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          component="img"
          src="/loading.svg"
          alt="Loading..."
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* Loading Text */}
      <Fade in timeout={500}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: '#FF7A00',
              mb: 1,
              animation: `${fadeInOut} 2s ease-in-out infinite`,
            }}
          >
            {message}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </Fade>
    </Box>
  );
};

// Alternative Skeleton Loading for Dashboard
export const DashboardSkeleton: React.FC = () => {
  return (
    <Box>
      {/* Stats Cards Skeleton */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        {[1, 2, 3, 4, 5, 6].map((item) => (
          <Box
            key={item}
            sx={{
              height: 140,
              borderRadius: 2,
              bgcolor: 'grey.100',
              animation: `${pulse} 1.5s ease-in-out infinite`,
              animationDelay: `${item * 0.1}s`,
            }}
          />
        ))}
      </Box>

      {/* Chart Skeleton */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
          gap: 3,
        }}
      >
        <Box
          sx={{
            height: 400,
            borderRadius: 2,
            bgcolor: 'grey.100',
            animation: `${pulse} 1.5s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            height: 400,
            borderRadius: 2,
            bgcolor: 'grey.100',
            animation: `${pulse} 1.5s ease-in-out infinite`,
            animationDelay: '0.2s',
          }}
        />
      </Box>
    </Box>
  );
};

export default LoadingAnimation;
